// "Run AI Presence" for a product: confirms the competitors to track and any
// prompts the brand always wants benchmarked before it starts. Prefilled with
// the product's saved hints; shows the brand-wide hints it inherits
// read-only; saves any change on the product, then starts the analysis. The
// first run builds the product's fixed benchmark prompt set; later runs
// reuse it so scores are comparable over time (staff can start a new
// benchmark version, which restarts the trend line).
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { productAiPresenceEndpoint, productAiPresenceSettingsEndpoint } from '../../api/endpoints';
import { AnalysisScheduleFields, AnalysisSchedule } from '../Analysis/AnalysisScheduleFields';
import { scheduleLabel } from '../../utils/plans';
import { useUser } from '../../hooks/useUser';
import { isInternal } from '../../utils';

interface Props {
  desireid: number;
  // The product's manufacturer brand — the run is stamped with it.
  brandId: number;
  productTitle?: string | null;
  onClose: () => void;
  onStarted?: () => void;
}

export function AiPresenceRunDialog({ desireid, brandId, productTitle, onClose, onStarted }: Props) {
  const { role } = useUser();
  const [loading, setLoading] = useState(true);
  const [competitors, setCompetitors] = useState('');
  const [savedCompetitors, setSavedCompetitors] = useState('');
  const [prompts, setPrompts] = useState('');
  const [savedPrompts, setSavedPrompts] = useState('');
  const [brand, setBrand] = useState<{ name: string | null; competitors: string | null; prompts: string | null }>({
    name: null,
    competitors: null,
    prompts: null,
  });
  const [regenerate, setRegenerate] = useState(false);
  // The product's schedule: how often AI Presence runs while a page of the
  // product is on Autopilot, and the day. Saved with the hints.
  const [schedule, setSchedule] = useState<AnalysisSchedule>({ interval: 'monthly', weekday: null, dayOfMonth: null });
  const [savedSchedule, setSavedSchedule] = useState<AnalysisSchedule>({ interval: 'monthly', weekday: null, dayOfMonth: null });
  const [maxInterval, setMaxInterval] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstant
      .get(`${productAiPresenceSettingsEndpoint(desireid)}?brandId=${brandId}`)
      .then((res) => {
        if (cancelled || !res.data) return;
        const c = res.data.aip_competitors || '';
        const p = res.data.aip_prompts || '';
        setCompetitors(c);
        setSavedCompetitors(c);
        setPrompts(p);
        setSavedPrompts(p);
        setBrand({
          name: res.data.brand_name || null,
          competitors: res.data.brand_aip_competitors || null,
          prompts: res.data.brand_aip_prompts || null,
        });
        const sched = {
          interval: res.data.aip_analysis_interval || 'monthly',
          weekday: res.data.aip_analysis_weekday !== undefined ? res.data.aip_analysis_weekday : null,
          dayOfMonth: res.data.aip_analysis_day_of_month !== undefined ? res.data.aip_analysis_day_of_month : null,
        };
        setSchedule(sched);
        setSavedSchedule(sched);
        setMaxInterval(res.data.max_interval || null);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [desireid, brandId]);

  const run = async () => {
    setStarting(true);
    try {
      const scheduleChanged =
        schedule.interval !== savedSchedule.interval ||
        schedule.weekday !== savedSchedule.weekday ||
        schedule.dayOfMonth !== savedSchedule.dayOfMonth;
      if (competitors.trim() !== savedCompetitors.trim() || prompts.trim() !== savedPrompts.trim() || scheduleChanged) {
        await axiosInstant.patch(`${productAiPresenceSettingsEndpoint(desireid)}?brandId=${brandId}`, {
          aip_competitors: competitors,
          aip_prompts: prompts,
          aip_analysis_interval: schedule.interval,
          aip_analysis_weekday: schedule.weekday,
          aip_analysis_day_of_month: schedule.dayOfMonth,
        });
      }
      await axiosInstant.post(productAiPresenceEndpoint(desireid), { brandId, regenerate_prompts: regenerate || undefined });
      toast.success('AI Presence analysis started — the score updates when it finishes (about a minute).');
      onStarted?.();
      onClose();
    } catch (e: any) {
      if (e?.response?.data?.error === 'IntervalExceedsPlanMax') {
        toast.error("That schedule is faster than the brand's plan allows.");
        setStarting(false);
        return;
      }
      const status = e?.response?.status;
      if (status === 409) {
        toast('An AI Presence analysis is already running for this product.');
        onStarted?.();
        onClose();
      } else if (status === 429) {
        toast.error("You've reached the hourly limit for AI Presence analyses. Try again later.");
      } else {
        toast.error("Couldn't start the AI Presence analysis. Please try again.");
      }
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open onClose={starting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span className="truncate">Run AI Presence{productTitle ? ` — ${productTitle}` : ''}</span>
        <IconButton aria-label="Close" size="small" onClick={onClose} disabled={starting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="text-[13px] text-text-2 mb-3">
          We ask Claude, Google Gemini and OpenAI the shopping questions buyers of this product ask (built from search demand,
          Consumer Voice and the product page) and measure how often each one surfaces and recommends it.
        </p>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Competitors to track"
          placeholder="e.g. JBL Charge 6, Sonos Move 2"
          value={competitors}
          onChange={(e) => setCompetitors(e.target.value)}
          disabled={loading || starting}
          helperText="Comma-separated (up to 10). Seeds the comparison prompts. Saved on the product."
          sx={{ mt: 0.5 }}
        />
        {brand.competitors ? (
          <p className="text-[12px] text-text-2 mt-2">
            Also inherited from {brand.name || 'the brand'}: <span className="font-semibold">{brand.competitors}</span>
          </p>
        ) : null}
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="Prompts to always include"
          placeholder="e.g. What is the best waterproof speaker for kayaking?"
          value={prompts}
          onChange={(e) => setPrompts(e.target.value)}
          disabled={loading || starting}
          helperText="One per line (up to 10). Added when the benchmark prompt set is built."
          sx={{ mt: 2 }}
        />
        {brand.prompts ? (
          <p className="text-[12px] text-text-2 mt-2">
            Also inherited from {brand.name || 'the brand'}: <span className="font-semibold">{brand.prompts.split('\n').join(' · ')}</span>
          </p>
        ) : null}
        <AnalysisScheduleFields
          label="AI Presence analysis interval"
          value={schedule}
          onChange={setSchedule}
          maxInterval={maxInterval}
          helperText={`Runs ${scheduleLabel(schedule.interval, schedule.weekday, schedule.dayOfMonth).toLowerCase()} while a page of this product is on Autopilot.`}
          disabled={loading || starting}
          className="mt-4"
        />
        {isInternal(role) ? (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} disabled={starting} />}
            label={<span className="text-[12.5px] text-text-2">Build a new benchmark prompt set (restarts the trend line)</span>}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={starting}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={run} disabled={loading || starting}>
          {starting ? 'Starting…' : 'Run analysis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
